'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import ProductCard from '@/components/ProductCard';
import AvailabilityBadge from '@/components/AvailabilityBadge';
import AddToCartButton from '@/components/cart/AddToCartButton';
import QuantityStepper from '@/components/cart/QuantityStepper';
import { getProductBySlug, getProductsByGame, getAvailability, ALL_PRODUCTS } from '@/lib/products';
import { useI18n } from '@/i18n/LocaleProvider';
import { formatDate, formatPrice } from '@/i18n/config';
import { localizeProduct } from '@/i18n/products';

export default function ProductPageContent() {
  const searchParams = useSearchParams();
  const slug = searchParams.get('slug');
  const sectionRef = useRef<HTMLDivElement>(null);
  const { locale, t } = useI18n();
  const [quantity, setQuantity] = useState(1);

  const baseProduct = slug ? getProductBySlug(slug) : null;
  const product = baseProduct ? localizeProduct(baseProduct, locale) : null;

  const relatedProducts = product
    ? getProductsByGame(product.gameSlug).filter(p => p.slug !== product.slug).slice(0, 3)
    : ALL_PRODUCTS.filter(p => p.featured).slice(0, 3);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05 }
    );
    const children = section.querySelectorAll('.stagger-child');
    children.forEach((child, i) => {
      (child as HTMLElement).style.transitionDelay = `${i * 80}ms`;
      observer.observe(child);
    });
    return () => observer.disconnect();
  }, [slug]);

  useEffect(() => setQuantity(1), [slug]);

  const typeColors: Record<string, string> = {
    'Booster Box': 'text-primary border-primary/30 bg-primary/10',
    'Booster Pack': 'text-accent border-accent/30 bg-accent/10',
    'Elite Trainer Box': 'text-purple-300 border-purple-500/30 bg-purple-500/10',
    'Bundle': 'text-teal-300 border-teal-500/30 bg-teal-500/10',
    'Collector Box': 'text-pink-300 border-pink-500/30 bg-pink-500/10',
  };

  const availability = product ? getAvailability(product) : 'sold_out';

  if (!product) {
    return (
      <main className="pt-36 pb-20 px-4 max-w-4xl mx-auto text-center">
        <div className="glass-card rounded-2xl p-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">{t.product.notFoundTitle}</h1>
          <p className="text-muted-foreground mb-8">{t.product.notFoundBody}</p>
          <Link href="/category" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
            {t.product.browseAllProducts}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main ref={sectionRef} className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8 stagger-child" aria-label="Breadcrumb">
          <Link href="/" className="hover:text-foreground transition-colors">{t.nav.home}</Link>
          <span>/</span>
          <Link href="/category" className="hover:text-foreground transition-colors">{t.nav.categories}</Link>
          <span>/</span>
          <Link href={`/category?game=${product.gameSlug}`} className="hover:text-foreground transition-colors">{product.game}</Link>
          <span>/</span>
          <span className="text-foreground font-medium line-clamp-1">{product.name}</span>
        </nav>

        {/* Product Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-20">
          {/* Left — Image */}
          <div className="stagger-child">
            <div className="relative aspect-square rounded-2xl overflow-hidden glass-card border border-border bg-muted/20">
              {/* Ambient glow behind image */}
              <div className="absolute inset-0 product-image-glow pointer-events-none" />
              <AppImage
                src={product.image}
                alt={product.imageAlt}
                fill
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="object-contain p-24 product-image-shadow"
                priority
              />
              {/* Top badges */}
              <div className="absolute top-4 left-4">
                <AvailabilityBadge product={product} size="md" />
              </div>
              <div className="absolute top-4 right-4">
                <span className={`text-xs px-3 py-1 rounded-full border font-semibold ${typeColors[product.type] || ''}`}>
                  {product.type}
                </span>
              </div>
            </div>

            {/* Availability notice under image */}
            {availability !== 'in_stock' && (
              <div className="mt-4 flex items-center gap-3 glass-card rounded-xl px-4 py-3">
                <svg className="w-5 h-5 text-accent flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-muted-foreground">
                  {availability === 'preorder' ? (
                    <><span className="text-foreground font-semibold">{t.shop.preorderTitle}</span> {t.shop.preorderBody}</>
                  ) : (
                    <><span className="text-foreground font-semibold">{t.product.restockTitle}</span> {t.product.restockBody}</>
                  )}
                </p>
              </div>
            )}
          </div>

          {/* Right — Details */}
          <div className="flex flex-col">
            {/* Game badge */}
            <div className="stagger-child mb-4">
              <Link
                href={`/category?game=${product.gameSlug}`}
                className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {product.game}
              </Link>
            </div>

            {/* Product name */}
            <div className="stagger-child">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-foreground leading-tight mb-3">
                {product.name}
              </h1>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-6">{product.set}</p>
            </div>

            {/* Price + sold out */}
            <div className="stagger-child flex items-center gap-4 mb-6 pb-6 border-b border-border">
              <span className="text-4xl font-extrabold accent-text-gradient">{formatPrice(product.price, locale)}</span>
              <AvailabilityBadge product={product} size="lg" />
            </div>

            {/* Description */}
            <div className="stagger-child mb-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-3">{t.product.description}</h3>
              <p className="text-muted-foreground leading-relaxed text-base">{product.description}</p>
            </div>

            {/* Product details */}
            <div className="stagger-child glass-card rounded-xl p-5 mb-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground mb-4">{t.product.details}</h3>
              <dl className="grid grid-cols-2 gap-y-3 gap-x-4">
                <div>
                  <dt className="text-xs text-muted-foreground">{t.product.tcg}</dt>
                  <dd className="text-sm font-semibold text-foreground">{product.game}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.product.set}</dt>
                  <dd className="text-sm font-semibold text-foreground">{product.set}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.product.type}</dt>
                  <dd className="text-sm font-semibold text-foreground">{product.type}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.product.condition}</dt>
                  <dd className="text-sm font-semibold text-foreground">{t.product.conditionValue}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.product.region}</dt>
                  <dd className="text-sm font-semibold text-foreground">{t.product.regionValue}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">{t.product.availability}</dt>
                  {availability === 'sold_out' && <dd className="text-sm font-semibold text-red-400">{t.product.outOfStock}</dd>}
                  {availability === 'preorder' && <dd className="text-sm font-semibold text-accent">{t.shop.preorder}</dd>}
                  {availability === 'in_stock' && <dd className="text-sm font-semibold text-emerald-500">{t.shop.inStock}</dd>}
                </div>
                {product.preorder && (
                  <div>
                    <dt className="text-xs text-muted-foreground">{t.shop.releaseDate}</dt>
                    <dd className="text-sm font-semibold text-foreground">{formatDate(product.preorder.releaseDate, locale)}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="stagger-child flex flex-col sm:flex-row sm:items-center gap-3">
              {availability !== 'sold_out' && (
                <>
                  <QuantityStepper value={quantity} onChange={setQuantity} />
                  <AddToCartButton product={product} quantity={quantity} size="lg" />
                </>
              )}
              <Link
                href={`/category?game=${product.gameSlug}`}
                className="py-4 px-6 rounded-full text-sm font-semibold border border-border text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all text-center"
              >
                {t.product.moreFrom(product.game)}
              </Link>
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="stagger-child">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="text-xs font-bold tracking-widest uppercase text-accent mb-1 block">{t.product.moreLikeThis}</span>
                <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">{t.product.related}</h2>
              </div>
              <Link
                href={`/category?game=${product.gameSlug}`}
                className="text-sm font-semibold text-primary hover:text-accent transition-colors"
              >
                {t.product.viewAll}
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedProducts.map(related => (
                <ProductCard key={related.id} product={related} />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}